import { createFileRoute, Link } from "../lib/router-compat";
import { PageShell } from "../components/PageShell";
import { cn } from "../lib/utils";

function Panel({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div className={cn("rounded-2xl border border-border/40 bg-card/40 backdrop-blur-sm hover:border-primary/40 transition-colors", className)}>
      {children}
    </div>
  );
}
import { Calendar, Clock, MapPin, ArrowUpRight } from "lucide-react";

export const Route = createFileRoute("/calendar")({
  head: () => ({
    meta: [
      { title: "Calendar — The Pan Institute" },
      { name: "description", content: "Steel band classes, events, and rehearsals at the Pan Institute. Contact us for full details." },
    ],
  }),
  component: CalendarPage,
});

const EVENTS = [
  {
    id: "spirit-of-america-alive-parade",
    kicker: "Parade · Atlantic Clarion",
    month: "Aug",
    day: "02",
    startLabel: "August 2, 2026",
    days: "Sunday",
    time: "10:30 am",
    title: "250 Spirit of America Alive",
    venue: "Brooksville",
    address: "Brooksville, Maine",
    body: "Atlantic Clarion performs in the 250 Spirit of America Alive parade.",
  },
  {
    id: "brooksville-historical-society-annual-celebration",
    kicker: "Annual Celebration · Atlantic Clarion",
    month: "Aug",
    day: "23",
    startLabel: "August 23, 2026",
    days: "Sunday",
    time: "2:00 – 3:00 pm",
    title: "Brooksville Historical Society Annual Celebration",
    venue: "Brooksville Historical Society",
    address: "Brooksville, Maine",
    body: "Atlantic Clarion performs at the Brooksville Historical Society annual celebration.",
  },
];

function CalendarPage() {
  return (
    <PageShell
      tag="Calendar"
      title={<>The <span className="text-primary">calendar</span>.</>}
      intro="Concerts, classes, open rehearsals, and festivals. Contact us for full details on any event."
    >
      <div className="space-y-10">
        {/* Featured / upcoming list */}
        <div className="flex items-baseline justify-between border-b border-foreground/10 pb-3">
          <div className="text-[10px] uppercase tracking-[0.4em] text-foreground/55">Upcoming</div>
          <Link
            to="/contact"
            className="text-[10px] uppercase tracking-[0.4em] text-primary inline-flex items-center gap-1 hover:gap-2 transition-all"
          >
            Inquire <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>

        <div className="space-y-6">
          {EVENTS.map((e) => (
            <Panel key={e.id} className="group relative overflow-hidden p-0">
              <div className="grid md:grid-cols-[180px_1fr] gap-0">
                {/* Date block */}
                <div className="relative bg-primary/5 border-b md:border-b-0 md:border-r border-foreground/10 p-6 flex flex-col justify-center items-center text-center">
                  <div className="text-[10px] uppercase tracking-[0.4em] text-primary mb-2">{e.month}</div>
                  <div className="font-display text-5xl leading-none">{e.day}</div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/55 mt-3">{e.days}</div>
                </div>

                {/* Body */}
                <div className="p-6 md:p-8">
                  <div className="text-[10px] uppercase tracking-[0.4em] text-primary mb-3">{e.kicker}</div>
                  <h3 className="font-display italic text-2xl md:text-3xl leading-tight">{e.title}</h3>

                  <div className="mt-5 grid sm:grid-cols-2 gap-3 text-sm">
                    <div className="flex items-start gap-2 text-foreground/70">
                      <Clock className="w-4 h-4 mt-0.5 text-primary/70 shrink-0" />
                      <div>
                        <div>{e.time}</div>
                        <div className="text-foreground/50 text-xs">{e.days}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 text-foreground/70">
                      <MapPin className="w-4 h-4 mt-0.5 text-primary/70 shrink-0" />
                      <div>
                        <div>{e.venue}</div>
                        <div className="text-foreground/50 text-xs">{e.address}</div>
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-foreground/65 mt-5 leading-relaxed max-w-prose">{e.body}</p>

                  <div className="mt-6 pt-5 border-t border-foreground/10">
                    <div className="text-[10px] uppercase tracking-[0.4em] text-foreground/45">{e.startLabel}</div>
                  </div>
                </div>
              </div>
            </Panel>
          ))}
        </div>

        {/* Footer note */}
        <Panel className="bg-foreground/[0.02] p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-primary/70" />
              <div>
                <div className="text-sm">More on the calendar soon.</div>
                <div className="text-xs text-foreground/55">Reach out for performance bookings, private lessons, or rehearsal visits.</div>
              </div>
            </div>
            <Link
              to="/contact"
              className="text-[10px] uppercase tracking-[0.4em] text-primary inline-flex items-center gap-1 hover:gap-2 transition-all"
            >
              Contact us <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
        </Panel>
      </div>
    </PageShell>
  );
}
